declare const Choices: any;

// Turns the ASP.NET field name "Addresses[0].GooglePlaceId" into the id ASP.NET would have
// generated for it, "Addresses_0__GooglePlaceId", so the input matches the rest of the form.
function nameToId(name: string): string {
  return name.replace(/\[/g, "_").replace(/\]/g, "__").replace(/\./g, "_");
}

export default function initLocationSearchInputs(inputElement: Element | null) {
  let inputs: Element[];

  if (inputElement == null) {
    inputs = Array.from(document.getElementsByClassName("location-input-select"));
  } else {
    inputs = [inputElement];
  }

  if (inputs.length == 0) return;

  for (let i = 0; i < inputs.length; i++) {

    let input = inputs[i];
    let id = input.id;
    let debounceTimeout;
    let debounceDelay = 300; // 300 ms delay

    let options = {
      searchPlaceholderValue: "SW19 - start typing here",
      noChoicesText: "Start typing a postcode",
      searchResultLimit: 20,
      removeItemButton: true
    };

    let newChoice = new Choices("#" + id, options);

    const select = newChoice.passedElement.element as HTMLSelectElement;

    // The hidden GooglePlaceId input must be a SIBLING of the select, not a child of it.
    // A <select> may only contain <option>/<optgroup>, so appending an <input> inside it was
    // invalid HTML - and it was destroyed by the wrapper removal in formTools.ts when the user
    // added another address.
    const container = (select.closest(".form-group") ?? select.parentElement) as HTMLElement;

    // Returns "" when the select is not bound to a PostalCodeId field. Without that guard the
    // replace below would be a no-op and we would create a hidden input sharing the select's own
    // name, which the model binder would then join into a single comma-separated value.
    function placeIdName(): string {
      const selectName = select.getAttribute("name") ?? "";
      if (selectName.indexOf("PostalCodeId") === -1) return "";

      return selectName.replace("PostalCodeId", "GooglePlaceId");
    }

    // Finds this row's hidden place id input, optionally creating it.
    //
    // The old code called document.getElementById(name) with the NAME
    // ("Addresses[0].GooglePlaceId") rather than the generated id
    // ("Addresses_0__GooglePlaceId"), so it always got null and appended a brand new input on
    // every selection. Re-picking a location three times posted three GooglePlaceId values, which
    // the model binder joined with commas into an unusable place id.
    function placeIdInput(create: boolean): HTMLInputElement | null {
      const name = placeIdName();
      if (!name) return null;

      let el = container.querySelector(
        'input.google-place-id[name="' + name.replace(/"/g, '\\"') + '"]'
      ) as HTMLInputElement | null;

      if (el == null && create) {
        el = document.createElement("input");
        el.type = "hidden";
        el.name = name;
        el.id = nameToId(name);
        el.classList.add("google-place-id");
        container.appendChild(el);
      }

      return el;
    }

    select.addEventListener("search", function (e: any) {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(async function () {
        let searchVal = e.detail.value;
        if (!searchVal || searchVal.length <= 2) return;

        // Send the country so Google biases results to it. This was omitted, so an Australian
        // user typing "Richmond" was offered the London one. Encode the term too - an unencoded
        // "&" or "#" silently truncated the search.
        const countryEl = (container.closest(".form-input-group-item") ?? container.closest("form"))
          ?.querySelector(".address-country-select") as HTMLInputElement | HTMLSelectElement | null;
        const country = countryEl?.value ?? "";

        let url = "/AjaxHelper/Select2PlaceNamesOrPostalCodesAsync?q=" + encodeURIComponent(searchVal);
        if (country) {
          url += "&country=" + encodeURIComponent(country);
        }

        let data = await fetch(url)
          .then(response => response.ok ? response.json() : { items: [] })
          .then(data => {
            return data?.items ?? [];
          })
          .catch(error => {
            return [];
          });

        newChoice.setChoices(data, 'value', 'label', true);
      }, debounceDelay);
    });

    select.addEventListener("addItem", function (e: any) {
      // customProperties is undefined for statically rendered <option>s, such as the already
      // selected location on page load - that must not clear or overwrite anything.
      let customProperties = e.detail?.customProperties;
      let placeId = customProperties?.placeId ?? customProperties?.googlePlaceId;

      if (!placeId) return;

      const el = placeIdInput(true);
      if (el) {
        // Set the property, not the attribute: on an input that already exists, setAttribute
        // does not update the value that gets submitted.
        el.value = placeId;
      }
    });

    // removeItemButton is enabled, so the user can clear their choice. Without this the stale
    // place id stays in the form and gets resolved on save for a location they removed.
    select.addEventListener("removeItem", function () {
      const el = placeIdInput(false);
      if (el) {
        el.value = "";
      }
    });
  }
}
